// Typed publish/subscribe event bus.
// Consumers declare an event map via the generic parameter:
//   const bus = new EventBus<MyEvents>();
//   bus.on("damage.dealt", (e) => ...);
//   bus.emit("damage.dealt", { amount: 50 });

export type EventMap = Record<string, unknown>;

type Handler<P> = (payload: P) => void;

export class EventBus<Events extends EventMap> {
  private handlers = new Map<keyof Events, Set<Handler<unknown>>>();

  on<K extends keyof Events>(type: K, handler: Handler<Events[K]>): () => void {
    let set = this.handlers.get(type);
    if (!set) {
      set = new Set();
      this.handlers.set(type, set);
    }
    set.add(handler as Handler<unknown>);
    return () => set!.delete(handler as Handler<unknown>);
  }

  emit<K extends keyof Events>(type: K, payload: Events[K]): void {
    const set = this.handlers.get(type);
    if (!set) return;
    // Copy before iterating — handlers may unsubscribe mid-iteration.
    for (const handler of [...set]) {
      (handler as Handler<Events[K]>)(payload);
    }
  }

  off<K extends keyof Events>(type: K, handler: Handler<Events[K]>): void {
    this.handlers.get(type)?.delete(handler as Handler<unknown>);
  }

  clear(): void {
    this.handlers.clear();
  }
}
